import React from 'react';
import {
  Box,
  Typography,
  Tooltip,
  Chip,
} from '@mui/material';
import { Storefront as StorefrontIcon } from '@mui/icons-material';

export interface BrandMetrics {
  id: string;
  name: string;
  color: string;
  revenue: number;
  royalties: number;
  compliance: number;
  growthRate: number;
}

interface BrandFilterBarProps {
  brands: BrandMetrics[];
  selectedBrandId: string;
  onBrandSelect: (brandId: string) => void;
}

const formatCompactCurrency = (value: number): string => {
  if (value >= 1_000_000) {
    const m = value / 1_000_000;
    return `$${m < 10 ? m.toFixed(2) : m.toFixed(1)}M`;
  }
  if (value >= 1_000) {
    return `$${(value / 1_000).toFixed(0)}K`;
  }
  return `$${Math.round(value).toLocaleString()}`;
};

const BRAND_DISPLAY_SCALE = 100;

const formatRevenue = (value: number): string => formatCompactCurrency(value * BRAND_DISPLAY_SCALE);

const formatRoyalties = (value: number): string => formatCompactCurrency(value * BRAND_DISPLAY_SCALE);

const BrandTooltipContent: React.FC<{ brand: BrandMetrics }> = ({ brand }) => (
  <Box sx={{ p: 0.5, minWidth: 160 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Revenue
      </Typography>
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#1976D2' }}>
        {formatRevenue(brand.revenue)}
      </Typography>
    </Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Royalties
      </Typography>
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#F57C00' }}>
        {formatRoyalties(brand.royalties)}
      </Typography>
    </Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Compliance
      </Typography>
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#7B1FA2' }}>
        {brand.compliance.toFixed(1)}%
      </Typography>
    </Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Growth
      </Typography>
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#2E7D32' }}>
        {brand.growthRate > 0 ? '+' : ''}{brand.growthRate.toFixed(1)}%
      </Typography>
    </Box>
  </Box>
);

const BrandTile: React.FC<{
  brand: BrandMetrics;
  isSelected: boolean;
  onClick: () => void;
}> = ({ brand, isSelected, onClick }) => (
  <Tooltip
    title={<BrandTooltipContent brand={brand} />}
    arrow
    placement="bottom"
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: 'background.paper',
          color: 'text.primary',
          boxShadow: 3,
          border: '1px solid',
          borderColor: 'divider',
          p: 1.5,
          '& .MuiTooltip-arrow': {
            color: 'background.paper',
            '&::before': {
              border: '1px solid',
              borderColor: 'divider',
            },
          },
        },
      },
    }}
  >
    <Box
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        borderRadius: 2,
        overflow: 'hidden',
        minWidth: 120,
        flex: '1 1 0',
        outline: isSelected ? `3px solid ${brand.color}` : '3px solid transparent',
        outlineOffset: 2,
        transition: 'all 0.2s ease',
        boxShadow: isSelected ? 3 : 1,
        transform: isSelected ? 'translateY(-2px)' : 'none',
        '&:hover': {
          boxShadow: 3,
          transform: 'translateY(-2px)',
        },
      }}
    >
      {/* Colored header */}
      <Box
        sx={{
          bgcolor: brand.color,
          px: 1.5,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
        }}
      >
        <StorefrontIcon sx={{ fontSize: 16, color: 'white', opacity: 0.9 }} />
        <Typography
          sx={{
            color: 'white',
            fontWeight: 700,
            fontSize: '0.75rem',
            letterSpacing: 0.3,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {brand.name}
        </Typography>
      </Box>

      {/* Revenue metric */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          px: 2,
          py: 1.5,
          textAlign: 'center',
        }}
      >
        <Typography
          sx={{
            fontSize: '0.65rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 1,
            color: 'text.secondary',
            mb: 0.5,
          }}
        >
          Revenue
        </Typography>
        <Typography
          sx={{
            fontSize: '1.25rem',
            fontWeight: 800,
            color: brand.color,
            lineHeight: 1,
          }}
        >
          {formatRevenue(brand.revenue)}
        </Typography>
      </Box>
    </Box>
  </Tooltip>
);

export const BrandFilterBar: React.FC<BrandFilterBarProps> = ({
  brands,
  selectedBrandId,
  onBrandSelect,
}) => {
  if (!brands || brands.length === 0) return null;

  const handleClick = (brandId: string) => {
    // Toggle off if already selected
    onBrandSelect(selectedBrandId === brandId ? '' : brandId);
  };

  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography
          sx={{
            fontSize: '0.7rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '1.5px',
            color: 'text.disabled',
          }}
        >
          Brand Summary
        </Typography>
        {selectedBrandId && (
          <Chip
            label={`Filtered: ${brands.find(b => b.id === selectedBrandId)?.name}`}
            size="small"
            onDelete={() => onBrandSelect('')}
            sx={{ fontSize: '0.7rem', height: 22 }}
          />
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          flexWrap: 'nowrap',
          width: '100%',
        }}
      >
        {brands.map((brand) => (
          <BrandTile
            key={brand.id}
            brand={brand}
            isSelected={selectedBrandId === brand.id}
            onClick={() => handleClick(brand.id)}
          />
        ))}
      </Box>
    </Box>
  );
};
